import type {
  Aircraft,
  AircraftInput,
  Evaluation,
  FlightInput,
  NightLightResult,
  Registration,
  RemoteIdResult,
  SubcategoryAssessment,
  SubcategoryResult,
  UkClass,
  Unmet,
} from "../types";
import { sources } from "./sources";
import type { Context, SubcategoryRule } from "./tables";
import {
  OPEN_CATEGORY_BELOW_GRAMS,
  classEquivalence,
  conditions,
  inForce,
  nightLight as nightLightRule,
  registration as registrationTable,
  remoteId as remoteIdTable,
  subcategories,
} from "./tables";

const UK_CLASSES: readonly UkClass[] = ["UK0", "UK1", "UK2", "UK3", "UK4", "UK5", "UK6"];

/**
 * Resolves the form's class mark into what the rules act on, for one date.
 * EU C-class marks become their UK equivalent until equivalence ends, after
 * which they are flagged as expired rather than guessed at. Nothing after this
 * point sees a C mark or a form value.
 */
export function resolveAircraft(input: AircraftInput, today: string): Aircraft {
  const { mass, camera, remoteIdExempt, classMark } = input;
  const aircraft = (ukClass: UkClass | null, origin: Aircraft["origin"]): Aircraft => ({
    mass,
    camera,
    remoteIdExempt,
    ukClass,
    origin,
    byWeight: origin === "unmarked" || origin === "privatelyBuilt",
  });

  if (classMark === "none") return aircraft(null, "unmarked");
  if (classMark === "private") return aircraft(null, "privatelyBuilt");
  if (classMark === "uk4-model") return aircraft("UK4", "model");
  const ukClass = UK_CLASSES.find((mark) => mark === classMark);
  if (ukClass) return aircraft(ukClass, "classMarked");
  const equivalent = UK_CLASSES.find((mark) => mark === classMark.replace(/^C/, "UK"));
  if (equivalent)
    return inForce(classEquivalence, today)
      ? aircraft(equivalent, "classMarked")
      : aircraft(null, "expiredEu");
  throw new Error(`Unknown class mark: ${classMark}`);
}

/**
 * Evaluates a complete questionnaire snapshot into facts, without wording or
 * the DOM; see present() for the text.
 *
 * This function deliberately fails closed: invalid masses throw, aircraft at or
 * above 25kg are out of scope, and marks the rules no longer recognise are refused.
 */
export function evaluate(
  input: FlightInput,
  today = new Date().toISOString().slice(0, 10),
): Evaluation {
  const { verifiedAsOf } = sources;
  if (!Number.isFinite(input.aircraft.mass) || input.aircraft.mass <= 0)
    throw new Error("Enter a valid aircraft mass.");
  if (input.aircraft.mass >= OPEN_CATEGORY_BELOW_GRAMS)
    return { scope: "tooHeavy", verifiedAsOf, limitGrams: OPEN_CATEGORY_BELOW_GRAMS };
  if (input.flight.indoor) return { scope: "indoor", verifiedAsOf };

  const context: Context = { ...input, aircraft: resolveAircraft(input.aircraft, today) };
  return {
    scope: "open",
    verifiedAsOf,
    aircraft: context.aircraft,
    registration: registration(context),
    subcategory: subcategory(context),
    remoteId: remoteId(context.aircraft, today),
    nightLight: input.flight.night ? nightLight(today) : null,
    conditions,
  };
}

function registration({ aircraft, pilot }: Context): Registration {
  const row = registrationTable.find(({ applies }) => applies(aircraft));
  if (!row) throw new Error("Registration table has no row for this aircraft.");
  const operatorRequired = row.operatorId === "required";
  return {
    flyerId: row.flyerId,
    operatorId: row.operatorId,
    operatorOnly: pilot.operatorOnly,
    adultOperatorNeeded: operatorRequired && pilot.age < sources.operatorId.minimumAge,
  };
}

/** Assesses every subcategory; the first with nothing unmet is the one that fits. */
function subcategory(context: Context): SubcategoryResult {
  const assessments = subcategories.map((rule) => assess(rule, context));
  return {
    fits: assessments.find(({ unmet }) => unmet.length === 0)?.id ?? null,
    assessments,
  };
}

function assess(rule: SubcategoryRule, context: Context): SubcategoryAssessment {
  const minimums: SubcategoryAssessment["minimums"] = {};
  const unmet: Unmet[] = rule.aircraft(context.aircraft) ? [] : [{ requirement: "aircraft" }];
  for (const { id, distance, met } of rule.requires) {
    const minimum = distance?.minimum(context);
    if (minimum !== undefined) minimums[id] = minimum;
    if (met(context)) continue;
    unmet.push(
      distance
        ? { requirement: id, minimum, actual: distance.actual(context.flight) }
        : { requirement: id },
    );
  }
  return { id: rule.id, minimums, unmet };
}

/**
 * Remote ID applies when a matching row is in force. An exemption only counts
 * when every matching row allows one; a row not yet in force is upcoming.
 */
function remoteId(aircraft: Aircraft, today: string): RemoteIdResult {
  if (aircraft.origin === "expiredEu") return { status: "assumed" };
  const rows = remoteIdTable.filter(({ applies }) => applies(aircraft));
  if (rows.length === 0) return { status: "notRequired" };
  if (aircraft.remoteIdExempt && rows.every(({ exemptable }) => exemptable))
    return { status: "exempt" };
  if (rows.some((row) => inForce(row, today))) return { status: "required" };
  const from = rows.map((row) => row.from ?? today).sort()[0] ?? today;
  return { status: "upcoming", from };
}

function nightLight(today: string): NightLightResult {
  const from = nightLightRule.from ?? today;
  return { status: inForce(nightLightRule, today) ? "required" : "upcoming", from };
}
